"use client";

import { z } from "zod";
import { useSearchParams } from "next/navigation";
import { signIn } from "next-auth/react";
import { FaEnvelope } from "react-icons/fa6";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useTranslations } from "next-intl";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { SuccessState } from "./SuccessState";
import { validateEmail } from "@/clients/api";
const EmailSignInFormSchema = z.object({
  email: z.string().email({
    message: "invalid_email"
  })
});
const EmailSignInForm = () => {
  const t = useTranslations("signup_form");
  // TODO do something to deny abusive behaviour of sending multiples emails
  const [emailsSentTo, setEmailsSentTo] = useState<string[]>([]);
  const searchParams = useSearchParams();
  const {
    control,
    handleSubmit,
    setFocus,
    setError,
    reset,
    formState: {
      isSubmitting,
      isSubmitSuccessful
    }
  } = useForm<z.infer<typeof EmailSignInFormSchema>>({
    resolver: zodResolver(EmailSignInFormSchema),
    defaultValues: {
      email: ""
    }
  });
  const fixWrongEmail = () => {
    reset({
      email: emailsSentTo[0]
    });
    // setTimeout was necessary to wait component renders "unsubmitted state"
    setTimeout(() => setFocus("email", {
      shouldSelect: true
    }), 1);
  };
  const onSubmit = async (values: z.infer<typeof EmailSignInFormSchema>) => {
    if (emailsSentTo.includes(values.email)) {
      setError("email", {
        type: "manual",
        message: "email_already_sent"
      });
      return false;
    }
    try {
      const callbackUrl = searchParams.get("callbackUrl");
      const response = await validateEmail(values.email);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "email_sign_in_error");
      }
      await signIn("email", {
        email: values.email,
        callbackUrl: callbackUrl || undefined,
        redirect: false
      });
      setEmailsSentTo(prev => [values.email, ...prev]);
    } catch (error) {
      console.error(error);
      setError("email", {
        type: "manual",
        message: (error as Error).message || "email_sign_in_error"
      });
    }
  };
  return isSubmitSuccessful ? <SuccessState onClick={fixWrongEmail} emailSentTo={emailsSentTo[0]} data-sentry-element="SuccessState" data-sentry-component="EmailSignInForm" data-sentry-source-file="EmailSignInForm.tsx" /> : <form noValidate className="w-full" onSubmit={handleSubmit(onSubmit)} data-sentry-component="EmailSignInForm" data-sentry-source-file="EmailSignInForm.tsx">
      <Controller control={control} name="email" render={({
      field,
      fieldState
    }) => <Input {...field} variant="underlined" className="mb-4" label="Email" type="email" isInvalid={fieldState.invalid} errorMessage={fieldState.error?.message ? t(fieldState.error.message) : undefined} />} data-sentry-element="Controller" data-sentry-source-file="EmailSignInForm.tsx" />
      <Button startContent={<FaEnvelope />} className="mb-6 w-full" color="primary" type="submit" isLoading={isSubmitting} data-sentry-element="Button" data-sentry-source-file="EmailSignInForm.tsx">
        <span>{t("button_label_prefix")} Email</span>
      </Button>
    </form>;
};
export { EmailSignInForm };